import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { deleteUser, postUser } from "../../helper/http";
import styles from "./EventTemplateEditor.module.css";
import { ToastService } from "../../service/toast.service";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faBook, faTrash } from "@fortawesome/free-solid-svg-icons";
import type {
  Primitive,
  Connection,
  Step,
  EditingElement,
  Module,
} from "../../types/TransactionTemplate.types";
import EditPanel from "../../components/TransactionTemplate/EditPanel/EditPanel";
import DeleteElementModal from "../../components/TransactionTemplate/Modals/DeleteStepConfirmModal/DeleteConfirmModal";
import { useLanguage } from "../../context/LanguageContext";
import ShapeGuideModal from "../../components/TransactionTemplate/Modals/ShapeGuideModal/ShapeGuideModal";

export default function EventTemplateEditor() {
  const { id } = useParams();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const svgRef = useRef<SVGSVGElement>(null);

  const [processAction, setProcessAction] = useState<any>(null);
  const [steps, setSteps] = useState<Step[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingElement, setEditingElement] = useState<EditingElement | null>(null);
  const [modules, setModules] = useState<Module[]>([]);
  const [showShapeGuide, setShowShapeGuide] = useState(false);
  const [deleteModal, setDeleteModal] = useState<{ 
    isOpen: boolean; 
    name: string; 
    onConfirm: () => void 
  } | null>(null);

  const textColor = "#000000";

  useEffect(() => {
    loadProcessData();
    loadModules();
  }, [id]);

  const loadModules = async () => {
    try {
      const res = await postUser("crud/list", { 
        module: "transaction", 
        per_page: "100" 
      });
      
      if (res.data) {
        setModules(
          res.data.map((t: any) => ({ 
            id: t.id, 
            name: `Transaction ${t.id}` 
          }))
        );
      }
    } catch (e) {
      console.error("Error loading modules:", e);
    }
  };

  const loadProcessData = async () => {
    if (!id || id === "new") {
      setLoading(false);
      return;
    }
    
    try {
      const response = await postUser("crud/list", {
        module: "process",
        filter: { processId: Number(id) }
      });

      if (response.data && response.data.length > 0) {
        const action = response.data[0];
        setProcessAction(action);
        
        const initialStep: Step = {
          id: "step-0",
          name: "Process Flow",
          event: {
            id: "event-0",
            name: action.eventType || "start",
            x: 60, 
            y: 80, 
            data: {
              eventType: action.eventType,
              eventFilterFunc: action.eventFilterFunc || "return true;",
              validations: action.validations || [],
              fndBody: action.fndBody || [],
            },
            from: []
          },
          primitives: [{
            id: "prim-0",
            name: action.transactionId 
              ? `Transaction ${action.transactionId}` 
              : "Select Transaction",
            x: 300, 
            y: 70,
            type: "primitive",
            data: { 
              transactionId: action.transactionId,
              id: action.transactionId,
            }
          }],
          connections: [{ 
            from: "event-0", 
            to: "prim-0", 
            type: "primitive" 
          }],
          y: 50,
          originalData: action,
          outgoingStepConnections: []
        };
        
        setSteps([initialStep]);
      } else {
        ToastService.error(t("processNotFound") || "Process not found");
        navigate("/processes");
      }
      
      setLoading(false);
    } catch (error) {
      console.error("Error loading process:", error);
      ToastService.error(t("loadError") || "Load error");
      setLoading(false);
    }
  };

  const saveProcess = async () => {
    const step = steps[0];
    if (!step || !processAction) {
      ToastService.error(t("noDataToSave") || "No data to save");
      return;
    }

    if (!step.event?.name || step.event.name.trim() === "") {
      ToastService.error(t("eventTypeRequired") || "Event type is required");
      return;
    }

    const transactionId = step.primitives[0]?.data?.transactionId;
    if (!transactionId) {
      ToastService.error(t("transactionRequired") || "Transaction is required");
      return;
    }

    try {
      const body: any = {
        id: processAction.id,
        processId: Number(id),
        eventType: step.event.name,
        transactionId: transactionId,
        eventFilterFunc: step.event.data?.eventFilterFunc || "return true;",
        validations: step.event.data?.validations || [],
        fndBody: step.event.data?.fndBody || [],
      };

      const response = await postUser("crud/upsert", {
        module: "process",
        body
      });

      if (response.body?.id) {
        setProcessAction({ ...processAction, ...body });
        ToastService.success(t("saved") || "Saved");
      }
    } catch (e) {
      console.error("Save error:", e);
      ToastService.error(t("saveError") || "Save error");
    }
  };

  const handleDeleteProcess = async () => {
    if (!processAction?.id) return;

    try {
      await deleteUser("crud/delete", {
        module: "process",
        id: processAction.id
      });

      ToastService.success(t("processDeleted") || "Process deleted");
      navigate("/processes");
    } catch (e) {
      console.error("Delete error:", e);
      ToastService.error(t("deleteError") || "Delete error");
    }
  };

  const renderEvent = (event: Step["event"], stepIndex: number) => {
    if (!event) return null;
    const baseX = event.x;
    const baseY = 50 + event.y;

    return (
      <g 
        onClick={() => setEditingElement({ 
          type: "event", 
          stepIndex, 
          data: event.data 
        })} 
        style={{ cursor: 'pointer' }}
      >
        <rect 
          x={baseX} 
          y={baseY} 
          width="120" 
          height="50" 
          fill="white" 
          stroke="black" 
          strokeWidth="2" 
          rx="4" 
        />
        <line 
          x1={baseX + 10} 
          y1={baseY} 
          x2={baseX + 10} 
          y2={baseY + 50} 
          stroke="black" 
          strokeWidth="2" 
        />
        <line 
          x1={baseX + 110} 
          y1={baseY} 
          x2={baseX + 110} 
          y2={baseY + 50} 
          stroke="black" 
          strokeWidth="2" 
        />
        <text 
          x={baseX + 60} 
          y={baseY + 25} 
          textAnchor="middle" 
          fontSize="11" 
          fontWeight="bold"
          fill={textColor}
        >
          Event
        </text>
        <text 
          x={baseX + 60} 
          y={baseY + 40} 
          textAnchor="middle" 
          fontSize="10" 
          fill={textColor}
        >
          {event.name}
        </text>
      </g>
    );
  };

  const renderPrimitive = (
    prim: Primitive, 
    stepIndex: number, 
    primIndex: number
  ) => {
    const baseX = prim.x;
    const baseY = 50 + prim.y;
    
    return (
      <g 
        onClick={() => setEditingElement({ 
          type: "primitive", 
          stepIndex, 
          primitiveIndex: primIndex, 
          data: prim.data 
        })} 
        style={{ cursor: 'pointer' }}
      >
        <rect 
          x={baseX} 
          y={baseY} 
          width="180" 
          height="60" 
          fill="white" 
          stroke="black" 
          strokeWidth="2" 
          rx="8" 
        />
        <text 
          x={baseX + 90} 
          y={baseY + 35} 
          textAnchor="middle" 
          fontSize="12" 
          fontWeight="500"
          fill={textColor}
        >
          {prim.name}
        </text>
      </g>
    );
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>{t("loading") || "Loading..."}</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button 
          className={styles.backButton} 
          onClick={() => navigate("/processes")}
        >
          <FontAwesomeIcon icon={faArrowLeft} /> {t("back") || "Back"}
        </button>
        
        <h1>{t("processEditor") || "Process Editor"} #{id}</h1>
        
        <div className={styles.headerActions}>
          <button 
            className={styles.deleteButton}
            onClick={() => setDeleteModal({
              isOpen: true,
              name: `Process ${id}`,
              onConfirm: handleDeleteProcess
            })}
          >
            <FontAwesomeIcon icon={faTrash} /> {t("delete") || "Delete"}
          </button>
          
          <button 
            className={styles.saveButton} 
            onClick={saveProcess}
          >
            {t("save") || "Save"}
          </button>
          
          <button 
            className={styles.guideButton} 
            onClick={() => setShowShapeGuide(true)}
          >
            <FontAwesomeIcon icon={faBook} /> {t("guide") || "Guide"}
          </button>
        </div>
      </div>

      <div className={styles.infoPanel}>
        <div className={styles.infoItem}>
          <strong>{t("eventType") || "Event Type"}:</strong> 
          <span>{processAction?.eventType || "—"}</span>
        </div>
        <div className={styles.infoItem}>
          <strong>{t("transaction") || "Transaction"}:</strong> 
          <span>
            {processAction?.transactionId 
              ? `Transaction ${processAction.transactionId}` 
              : t("noTransaction") || "No transaction"}
          </span>
        </div>
        <div className={styles.infoItem}>
          <strong>{t("filter") || "Filter"}:</strong> 
          <code>{processAction?.eventFilterFunc || "return true;"}</code>
        </div>
      </div>

      <div className={styles.canvas}>
        <svg ref={svgRef} width="100%" height="800">
          <defs>
            <marker 
              id="arrowhead" 
              markerWidth="10" 
              markerHeight="10" 
              refX="9" 
              refY="3" 
              orient="auto"
            >
              <polygon points="0 0, 10 3, 0 6" fill="black" />
            </marker>
          </defs>
          
          {steps.map((step, sIdx) => (
            <g key={step.id}>
              {step.connections.map((conn, cIdx) => (
                <line 
                  key={cIdx} 
                  x1={180} 
                  y1={155} 
                  x2={300} 
                  y2={155} 
                  stroke="black" 
                  strokeWidth="2" 
                  markerEnd="url(#arrowhead)" 
                />
              ))}
              
              {renderEvent(step.event, sIdx)}
              {step.primitives.map((p, pIdx) => 
                renderPrimitive(p, sIdx, pIdx)
              )}
            </g>
          ))}
        </svg>
      </div>

      <EditPanel
        isOpen={!!editingElement}
        type={editingElement?.type || "primitive"}
        data={editingElement?.data || {}}
        onClose={() => setEditingElement(null)}
        onSave={(newData) => {
          setSteps(prev => {
            const next = [...prev];
            
            if (editingElement?.type === 'event') {
              next[0].event!.name = newData.eventType || newData.name;
              next[0].event!.data = {
                eventType: newData.eventType || newData.name,
                eventFilterFunc: newData.eventFilterFunc || "return true;",
                validations: newData.validations || [],
                fndBody: newData.fndBody || [],
              };
            } else {
              const transactionId = newData.id || newData.transactionId;
              next[0].primitives[0].data.transactionId = transactionId;
              next[0].primitives[0].data.id = transactionId;
              next[0].primitives[0].name = transactionId 
                ? `Transaction ${transactionId}` 
                : "Select Transaction";
            }
            
            return next;
          });
          setEditingElement(null);
        }}
        modules={modules}
      />

      {showShapeGuide && (
        <ShapeGuideModal 
          isOpen={showShapeGuide} 
          onClose={() => setShowShapeGuide(false)} 
        />
      )}

      {deleteModal && (
        <DeleteElementModal
          isOpen={deleteModal.isOpen}
          elementType="transaction"
          elementName={deleteModal.name}
          onConfirm={() => {
            deleteModal.onConfirm();
            setDeleteModal(null);
          }}
          onCancel={() => setDeleteModal(null)}
        />
      )}
    </div>
  );
}